import type {
  GetAgencyTimesheetValueByIdParams,
  GetAgencyTimesheetValueByIdResponse,
  CreateAgencyTimesheetValueByIdParams,
  CreateAgencyTimesheetValueByIdResponse,
} from '@nxsys/types/api';
import {
  ApproveExpenseEmployeeParams,
  ApproveExpenseEmployeeResponse,
  CalculateExpenseTotalParams,
  CalculateExpenseTotalResponse,
  CisVerifyParams,
  CisVerifyResponse,
  CreateAttachmentOrderEmployeeParams,
  CreateAttachmentOrderEmployeeResponse,
  CreateBIKEmployeeParams,
  CreateBIKEmployeeResponse,
  CreateDeductionEmployeeParams,
  CreateDeductionEmployeeResponse,
  CreateDocumentEmployeeParams,
  CreateDocumentEmployeeResponse,
  CreateExpenseEmployeeParams,
  CreateExpenseEmployeeResponse,
  CreateMessageEmployeeParams,
  CreateMessageEmployeeResponse,
  CreateNoteEmployeeParams,
  CreateNoteEmployeeResponse,
  CreatePaymentEmployeeParams,
  CreatePaymentEmployeeResponse,
  CreateReplyMessageEmployeeParams,
  CreateReplyMessageEmployeeResponse,
  DeleteBIKEmployeeResponse,
  DeleteDocumentEmployeeResponse,
  DeleteNoteEmployeeResponse,
  DeleteSubcontractorParams,
  DeleteSubcontractorResponse,
  DownloadP60EmployeeParams,
  DownloadP60EmployeeResponse,
  DownloadPayslipEmployeeResponse,
  GetPayslipDetailResponse,
  GetPayslipHistoryResponse,
  ExportAllEmployeeParams,
  ExportAllEmployeeResponse,
  ExportEmployeeParams,
  ExportEmployeeResponse,
  FetchSmartPensionParams,
  FetchSmartPensionResponse,
  GetBackOfficeByEmployeeIdResponse,
  GetBankInformationByEmployeeIdResponse,
  GetEmployeeByIdResponse,
  GetExpensePotEmployeeParams,
  GetExpensePotEmployeeResponse,
  GetPayrollSettingByEmployeeIdResponse,
  GetPensionByEmployeeIdResponse,
  GetYTDValueByEmployeeIdParams,
  GetYTDValueByEmployeeIdResponse,
  RejectExpenseEmployeeParams,
  RejectExpenseEmployeeResponse,
  ResendLoginIdMultipleParams,
  ResendLoginIdMultipleResponse,
  SendOnboardingParams,
  SendOnboardingResponse,
  SendEmailP60EmployeeParams,
  SendEmailP60EmployeeResponse,
  SubmitExpenseEmployeeParams,
  SubmitExpenseEmployeeResponse,
  UpdateAttachmentOrderEmployeeParams,
  UpdateAttachmentOrderEmployeeResponse,
  UpdateBackOfficeByEmployeeIdParams,
  UpdateBackOfficeByEmployeeIdResponse,
  UpdateBankByEmployeeIdParams,
  UpdateBankByEmployeeIdResponse,
  UpdateBIKEmployeeParams,
  UpdateBIKEmployeeResponse,
  UpdateDeductionEmployeeParams,
  UpdateDeductionEmployeeResponse,
  UpdateDocumentEmployeeParams,
  UpdateDocumentEmployeeResponse,
  UpdateEmployeeByIdParams,
  UpdateEmployeeByIdResponse,
  UpdateHolidayPayBalanceByIdParams,
  UpdateHolidayPayBalanceByIdResponse,
  UpdateNoteEmployeeParams,
  UpdateNoteEmployeeResponse,
  UpdateOnHoldEmployeeParams,
  UpdateOnHoldEmployeeResponse,
  UpdatePaymentEmployeeParams,
  UpdatePaymentEmployeeResponse,
  UpdatePensionByEmployeeIdParams,
  UpdatePensionByEmployeeIdResponse,
  UpdateSettingEmployeeByIdParams,
  UpdateSettingEmployeeByIdResponse,
  CreateEmployeeParams,
  CreateEmployeeResponse,
  GetEmployeeIrelandPrivatePensionResponse,
  GetAvailableCompanyPensionSettingsResponse,
  CreateEmployeeIrelandPrivatePensionParams,
  CreateEmployeeIrelandPrivatePensionResponse,
  UpdateEmployeeIrelandPrivatePensionParams,
  UpdateEmployeeIrelandPrivatePensionResponse,
} from '@nxsys/types/api';
import { IEmployeeIrelandPrivatePension } from '@nxsys/types/models/pension';
import axios from '../axios';
import { endPoints } from '../endpoints';

export const exportEmployeeApi = async (
  params: ExportEmployeeParams,
): Promise<ExportEmployeeResponse> => {
  const _params = { page: params.page, size: params.size };
  const response = await axios.put(
    endPoints.employee.exportExcel,
    { ids: params.ids },
    {
      params: _params,
    },
  );
  return response.data;
};

export const exportAllEmployeeApi = async (
  params: ExportAllEmployeeParams,
): Promise<ExportAllEmployeeResponse> => {
  const response = await axios.get(endPoints.employee.exportExcelV2, {
    params,
  });
  return response.data;
};

export const resendLoginIdMultipleApi = async (
  params: ResendLoginIdMultipleParams,
): Promise<ResendLoginIdMultipleResponse> => {
  const response = await axios.post(endPoints.employee.resendLoginId, params);
  return response.data;
};

export const deleteSubcontractorApi = async (
  data: DeleteSubcontractorParams,
): Promise<DeleteSubcontractorResponse> => {
  const response = await axios.delete(endPoints.employee.delete, { data });
  return response.data;
};

export const sendOnboardingApi = async (
  params: SendOnboardingParams,
): Promise<SendOnboardingResponse> => {
  const response = await axios.put(endPoints.employee.sendOnboarding, params);
  return response.data;
};

export const cisVerifyApi = async (
  params: CisVerifyParams,
): Promise<CisVerifyResponse> => {
  const response = await axios.post(endPoints.employee.verify, params);
  return response.data;
};

export const fetchSmartPensionApi = async (
  params: FetchSmartPensionParams,
): Promise<FetchSmartPensionResponse> => {
  const response = await axios.get(
    endPoints.employee.fetchSmartPensionByCompanyId,
    { params },
  );
  return response.data;
};

export const fetchSmartPensionByEmployeeApi = async (
  params: { subcontractorId: string },
): Promise<{ success: boolean }> => {
  const response = await axios.get(
    endPoints.employee.fetchSmartPensionByEmployeeId,
    { params },
  );
  return response.data;
};

export const validateLeaverIssuesApi = async (
  params: { ids: string[] },
): Promise<any> => {
  const response = await axios.post(
    endPoints.employment.validateLeaverIssues,
    params,
  );
  return response.data;
};

export const submitLeaverIssuesApi = async (
  params: { ids: string[] },
  queryParams: { dateLeft: string; reason: string },
): Promise<any> => {
  const response = await axios.post(
    endPoints.employment.submitLeaverIssues,
    params,
    { params: queryParams },
  );
  return response.data;
};

export const getRPNListByEmployeeIdApi = async (
  employeeId: string,
  params: Record<string, any>,
): Promise<any> => {
  const response = await axios.get(
    `${endPoints.employee.rpn}/${employeeId}`,
    { params },
  );
  return response.data;
};

export const updateRPNByEmployeeIdApi = async (
  employeeId: string,
  params: Record<string, any>,
): Promise<any> => {
  const response = await axios.post(
    `${endPoints.employee.rpn}/${employeeId}`,
    params,
  );
  return response.data;
};

export const createRPNEmployeesApi = async (
  params: { ids: string[] },
): Promise<any> => {
  const response = await axios.post(endPoints.rpn.createNew, params);
  return response.data;
};

export const updateOnHoldEmployeeApi = async (
  id: string,
  params: UpdateOnHoldEmployeeParams,
): Promise<UpdateOnHoldEmployeeResponse> => {
  const response = await axios.patch(endPoints.employee.onHold(id), params);
  return response.data;
};

export const getEmployeeByIdApi = async (
  id: string,
): Promise<GetEmployeeByIdResponse> => {
  const response = await axios.get(`${endPoints.employee.get}/${id}`);
  return response.data;
};

export const updateEmployeeByIdApi = async (
  id: string,
  params: UpdateEmployeeByIdParams,
): Promise<UpdateEmployeeByIdResponse> => {
  const response = await axios.put(`${endPoints.employee.get}/${id}`, params);
  return response.data;
};

export const getBankInformationByEmployeeIdApi = async (
  id: string,
): Promise<GetBankInformationByEmployeeIdResponse> => {
  const response = await axios.get(`${endPoints.employee.getBank}/${id}`);
  return response.data;
};

export const updateBankByEmployeeIdApi = async (
  id: string,
  params: UpdateBankByEmployeeIdParams,
): Promise<UpdateBankByEmployeeIdResponse> => {
  const response = await axios.post(
    `${endPoints.employee.getBank}/${id}`,
    params,
  );
  return response.data;
};

export const getPayrollSettingByEmployeeIdApi = async (
  id: string,
): Promise<GetPayrollSettingByEmployeeIdResponse> => {
  const response = await axios.get(
    `${endPoints.employee.payrollSetting}/${id}`,
  );
  return response.data;
};

// Margin band table for a subcontractor. Same `timesheet_values` resource as the
// agency one, so the agency request/response types are reused as-is.
export const getEmployeeTimesheetValueByIdApi = async (
  id: string,
  params: GetAgencyTimesheetValueByIdParams,
): Promise<GetAgencyTimesheetValueByIdResponse> => {
  const response = await axios.get(
    `${endPoints.employee.timesheetValue}/${id}`,
    { params },
  );
  return response.data;
};

export const createEmployeeTimesheetValueByIdApi = async (
  id: string,
  params: CreateAgencyTimesheetValueByIdParams,
): Promise<CreateAgencyTimesheetValueByIdResponse> => {
  const response = await axios.post(
    `${endPoints.employee.timesheetValue}/${id}`,
    params,
  );
  return response.data;
};

export const updateSettingEmployeeByIdApi = async (
  id: string,
  params: UpdateSettingEmployeeByIdParams,
): Promise<UpdateSettingEmployeeByIdResponse> => {
  const response = await axios.post(
    `${endPoints.employee.payrollSetting}/${id}`,
    params,
  );
  return response.data;
};

export const updateHolidayPayBalanceByIdApi = async (
  id: string,
  params: UpdateHolidayPayBalanceByIdParams,
): Promise<UpdateHolidayPayBalanceByIdResponse> => {
  const response = await axios.post(endPoints.employee.holidayPay(id), params);
  return response.data;
};

export const downloadP60EmployeeApi = async (
  id: string,
  params: DownloadP60EmployeeParams,
): Promise<DownloadP60EmployeeResponse> => {
  const response = await axios.post(
    endPoints.employee.downloadP60(id),
    {},
    {
      params,
    },
  );
  return response.data;
};

export const sendEmailP60EmployeeApi = async (
  id: string,
  params: SendEmailP60EmployeeParams,
): Promise<SendEmailP60EmployeeResponse> => {
  const response = await axios.post(
    endPoints.employee.sendEmailP60(id),
    {},
    {
      params,
    },
  );
  return response.data;
};

export const deleteDocumentEmployeeApi = async (
  id: string,
  employeeId: string,
): Promise<DeleteDocumentEmployeeResponse> => {
  const response = await axios.delete(
    `${endPoints.employee.document}/${employeeId}/${id}`,
  );
  return response.data;
};

export const createDocumentEmployeeApi = async (
  id: string,
  params: CreateDocumentEmployeeParams,
): Promise<CreateDocumentEmployeeResponse> => {
  const response = await axios.post(
    `${endPoints.employee.document}/${id}`,
    params,
  );
  return response.data;
};

export const updateDocumentEmployeeApi = async (
  id: string,
  employeeId: string,
  params: UpdateDocumentEmployeeParams,
): Promise<UpdateDocumentEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.employee.document}/${employeeId}/${id}`,
    params,
  );
  return response.data;
};

export const createNoteEmployeeApi = async (
  id: string,
  params: CreateNoteEmployeeParams,
): Promise<CreateNoteEmployeeResponse> => {
  const response = await axios.post(`${endPoints.employee.note}/${id}`, params);
  return response.data;
};

export const updateNoteEmployeeApi = async (
  id: string,
  employeeId: string,
  params: UpdateNoteEmployeeParams,
): Promise<UpdateNoteEmployeeResponse> => {
  const response = await axios.put(`${endPoints.employee.note}/${employeeId}`, {
    ...params,
    noteId: id,
  });
  return response.data;
};

export const deleteNoteEmployeeApi = async (
  id: string,
  employeeId: string,
): Promise<DeleteNoteEmployeeResponse> => {
  const response = await axios.delete(
    `${endPoints.employee.note}/${employeeId}`,
    {
      data: { noteId: id },
    },
  );
  return response.data;
};

export const createPaymentEmployeeApi = async (
  id: string,
  params: CreatePaymentEmployeeParams,
): Promise<CreatePaymentEmployeeResponse> => {
  const response = await axios.post(
    `${endPoints.employee.paymentsSetting}/${id}`,
    params,
  );
  return response.data;
};

export const updatePaymentEmployeeApi = async (
  id: string,
  employeeId: string,
  params: UpdatePaymentEmployeeParams,
): Promise<UpdatePaymentEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.employee.paymentsSetting}/${employeeId}/${id}`,
    params,
  );
  return response.data;
};

export const createDeductionEmployeeApi = async (
  id: string,
  params: CreateDeductionEmployeeParams,
  isIreland?: boolean,
): Promise<CreateDeductionEmployeeResponse> => {
  const endpoint = isIreland
    ? endPoints.employee.deductionSettingIreland
    : endPoints.employee.deductionSetting;
  const response = await axios.post(`${endpoint}/${id}`, params);
  return response.data;
};

export const updateDeductionEmployeeApi = async (
  id: string,
  employeeId: string,
  params: UpdateDeductionEmployeeParams,
  isIreland?: boolean,
): Promise<UpdateDeductionEmployeeResponse> => {
  const endpoint = isIreland
    ? endPoints.employee.deductionSettingIreland
    : endPoints.employee.deductionSetting;
  const response = await axios.put(`${endpoint}/${employeeId}/${id}`, params);
  return response.data;
};

export const downloadPayslipEmployeeApi = async (
  id: string,
): Promise<DownloadPayslipEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.employee.viewPdfSelfBillView}/${id}`,
  );
  return response.data;
};

export const getPayslipHistoryApi = async (
  subcontractorId: string,
): Promise<GetPayslipHistoryResponse> => {
  const response = await axios.get(
    `${endPoints.employee.selfBill}/${subcontractorId}`,
    { params: { page: 0, size: 3 } },
  );
  return response.data;
};

export const getPayslipDetailApi = async (
  selfBillId: string,
): Promise<GetPayslipDetailResponse> => {
  const response = await axios.get(
    endPoints.employee.getSelfBillDetail(selfBillId),
  );
  return response.data;
};

export const getBackOfficeByEmployeeIdApi = async (
  id: string,
): Promise<GetBackOfficeByEmployeeIdResponse> => {
  const response = await axios.get(`${endPoints.backOffice.main}/${id}`);
  return response.data;
};

export const updateBackOfficeByEmployeeIdApi = async (
  id: string,
  params: UpdateBackOfficeByEmployeeIdParams,
): Promise<UpdateBackOfficeByEmployeeIdResponse> => {
  const response = await axios.post(
    `${endPoints.backOffice.main}/${id}`,
    params,
  );
  return response.data;
};

export const createAttachmentOrderEmployeeApi = async (
  params: CreateAttachmentOrderEmployeeParams,
): Promise<CreateAttachmentOrderEmployeeResponse> => {
  const response = await axios.post(
    `${endPoints.attachmentOrder.main}`,
    params,
  );
  return response.data;
};

export const updateAttachmentOrderEmployeeApi = async (
  id: string,
  params: UpdateAttachmentOrderEmployeeParams,
): Promise<UpdateAttachmentOrderEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.attachmentOrder.main}/${id}`,
    params,
  );
  return response.data;
};

export const getPensionByEmployeeIdApi = async (
  id: string,
): Promise<GetPensionByEmployeeIdResponse> => {
  const response = await axios.get(`${endPoints.employee.pension}/${id}`);
  return response.data;
};

export const updatePensionByEmployeeIdApi = async (
  id: string,
  params: UpdatePensionByEmployeeIdParams,
): Promise<UpdatePensionByEmployeeIdResponse> => {
  const response = await axios.post(
    `${endPoints.employee.pension}/${id}`,
    params,
  );
  return response.data;
};

// Employee Ireland Private Pension APIs
export const getEmployeeIrelandPrivatePensionApi = async (
  subcontractorId: string,
): Promise<GetEmployeeIrelandPrivatePensionResponse> => {
  const response = await axios.get(
    endPoints.employee.irelandPrivatePension(subcontractorId),
  );
  return response.data;
};

export const getAvailableCompanyPensionSettingsApi = async (
  subcontractorId: string,
): Promise<GetAvailableCompanyPensionSettingsResponse> => {
  const response = await axios.get(
    `${endPoints.employee.irelandPrivatePension(subcontractorId)}/available-company-settings`,
  );
  return response.data;
};

export const createEmployeeIrelandPrivatePensionApi = async (
  subcontractorId: string,
  params: CreateEmployeeIrelandPrivatePensionParams,
): Promise<CreateEmployeeIrelandPrivatePensionResponse> => {
  const response = await axios.post(
    endPoints.employee.irelandPrivatePension(subcontractorId),
    params,
  );
  return response.data;
};

export const updateEmployeeIrelandPrivatePensionApi = async (
  subcontractorId: string,
  settingId: number,
  params: UpdateEmployeeIrelandPrivatePensionParams,
): Promise<UpdateEmployeeIrelandPrivatePensionResponse> => {
  const response = await axios.put(
    `${endPoints.employee.irelandPrivatePension(subcontractorId)}/${settingId}`,
    params,
  );
  return response.data;
};

export const deleteEmployeeIrelandPrivatePensionApi = async (
  subcontractorId: string,
  settingId: number,
): Promise<void> => {
  await axios.delete(
    `${endPoints.employee.irelandPrivatePension(subcontractorId)}/${settingId}`,
  );
};

export const toggleActiveEmployeeIrelandPrivatePensionApi = async (
  subcontractorId: string,
  settingId: number,
): Promise<IEmployeeIrelandPrivatePension> => {
  const response = await axios.post(
    `${endPoints.employee.irelandPrivatePension(subcontractorId)}/${settingId}/toggle-active`,
  );
  return response.data;
};

export const approveExpenseEmployeeApi = async (
  params: ApproveExpenseEmployeeParams,
): Promise<ApproveExpenseEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.employee.expense}/approve`,
    params,
  );
  return response.data;
};

export const rejectExpenseEmployeeApi = async (
  params: RejectExpenseEmployeeParams,
): Promise<RejectExpenseEmployeeResponse> => {
  const response = await axios.put(
    `${endPoints.employee.expense}/reject`,
    params,
  );
  return response.data;
};

export const calculateExpenseTotalApi = async (
  id: string,
  params: CalculateExpenseTotalParams,
): Promise<CalculateExpenseTotalResponse> => {
  const response = await axios.get(
    `${endPoints.employee.expense}/${id}/calculate-total`,
    { params },
  );
  return response.data;
};

export const createExpenseEmployeeApi = async (
  id: string,
  params: CreateExpenseEmployeeParams,
): Promise<CreateExpenseEmployeeResponse> => {
  const response = await axios.post(
    `${endPoints.employee.expense}/${id}`,
    params,
  );
  return response.data;
};

export const createMessageEmployeeApi = async (
  params: CreateMessageEmployeeParams,
): Promise<CreateMessageEmployeeResponse> => {
  const response = await axios.post(endPoints.employee.adminNewMessage, params);
  return response.data;
};

export const createReplyMessageEmployeeApi = async (
  id: number,
  params: CreateReplyMessageEmployeeParams,
): Promise<CreateReplyMessageEmployeeResponse> => {
  const response = await axios.post(
    `${endPoints.message.all}/${id}/reply-message`,
    params,
  );
  return response.data;
};

export const createBIKEmployeeApi = async (
  params: CreateBIKEmployeeParams,
): Promise<CreateBIKEmployeeResponse> => {
  const response = await axios.post(endPoints.employee.bik, params);
  return response.data;
};

export const updateBIKEmployeeApi = async (
  id: number,
  params: UpdateBIKEmployeeParams,
): Promise<UpdateBIKEmployeeResponse> => {
  const response = await axios.put(`${endPoints.employee.bik}/${id}`, params);
  return response.data;
};

export const deleteBIKEmployeeApi = async (
  id: number,
): Promise<DeleteBIKEmployeeResponse> => {
  const response = await axios.delete(`${endPoints.employee.bik}/${id}`);
  return response.data;
};

export const getYTDValueByEmployeeIdApi = async (
  id: string,
  params: GetYTDValueByEmployeeIdParams,
): Promise<GetYTDValueByEmployeeIdResponse> => {
  const response = await axios.get(`${endPoints.employee.ytdValues(id)}`, {
    params,
  });
  return response.data;
};

export const getExpensePotEmployeeApi = async (
  id: string,
  params: GetExpensePotEmployeeParams,
): Promise<GetExpensePotEmployeeResponse> => {
  const response = await axios.get(`${endPoints.employee.expensePot(id)}`, {
    params,
  });
  return response.data;
};

export const submitExpenseEmployeeApi = async (
  params: SubmitExpenseEmployeeParams,
): Promise<SubmitExpenseEmployeeResponse> => {
  const response = await axios.post(endPoints.employee.submitExpense, params);
  return response.data;
};

export const createEmployeeApi = async (
  params: CreateEmployeeParams,
): Promise<CreateEmployeeResponse> => {
  const response = await axios.post(endPoints.employee.getV2, params);
  return response.data;
};
